import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Shield, 
  Lock, 
  Eye, 
  Users, 
  Database, 
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const PrivacyPolicy = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "15 Aralık 2024";

  const sections = [
    {
      id: "introduction",
      title: "1. Giriş",
      icon: <Shield className="w-5 h-5" />,
      content: (
        <div>
          <p className="mb-4">
            Beetrax olarak, kullanıcılarımızın gizliliğini korumaya özen gösteririz. Bu Gizlilik Politikası, 
            Beetrax platformunu kullandığınızda kişisel verilerinizin nasıl toplandığını, kullanıldığını, 
            saklandığını ve korunduğunu açıklamaktadır.
          </p>
          <p className="mb-4">
            Bu politika, <strong>www.beetrax.com</strong> web sitesi ve ilgili tüm hizmetlerimiz için geçerlidir. 
            Hizmetlerimizi kullanarak, bu politikada açıklanan uygulamaları kabul etmiş olursunuz.
          </p>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'}`}>
            <div className="flex items-start">
              <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <div>
                <h4 className={`font-medium ${isDark ? 'text-blue-200' : 'text-blue-800'} mb-1`}>
                  Güvenlik Taahhüdümüz
                </h4>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  Verilerinizi korumak için endüstri standardı güvenlik önlemleri kullanıyoruz.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "data-collection",
      title: "2. Toplanan Veriler",
      icon: <Database className="w-5 h-5" />,
      content: (
        <div>
          <h4 className="font-medium mb-3">2.1 Kişisel Bilgiler</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Ad, soyad ve e-posta adresi</li>
            <li>Telefon numarası (opsiyonel)</li>
            <li>Şirket bilgileri</li>
            <li>Profil fotoğrafı (opsiyonel)</li>
          </ul>

          <h4 className="font-medium mb-3">2.2 Kullanım Verileri</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Platform kullanım istatistikleri</li>
            <li>Oturum süreleri ve sıklığı</li>
            <li>Özellik kullanım verileri</li>
            <li>Hata raporları ve performans verileri</li>
          </ul>

          <h4 className="font-medium mb-3">2.3 Teknik Veriler</h4>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>IP adresi</li>
            <li>Tarayıcı türü ve versiyonu</li>
            <li>İşletim sistemi</li>
            <li>Cihaz bilgileri</li>
          </ul>
        </div>
      )
    },
    {
      id: "data-usage",
      title: "3. Verilerin Kullanımı",
      icon: <Eye className="w-5 h-5" />,
      content: (
        <div>
          <p className="mb-4">Topladığımız verileri aşağıdaki amaçlarla kullanırız:</p>
          
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h4 className="font-medium mb-2">🔧 Hizmet Sağlama</h4>
              <p className="text-sm">Platform özelliklerini sağlamak ve hesabınızı yönetmek</p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h4 className="font-medium mb-2">📧 İletişim</h4>
              <p className="text-sm">Önemli güncellemeler, destek ve bilgilendirmeler</p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h4 className="font-medium mb-2">📊 Geliştirme</h4>
              <p className="text-sm">Platformumuzu iyileştirmek ve yeni özellikler geliştirmek</p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h4 className="font-medium mb-2">🛡️ Güvenlik</h4>
              <p className="text-sm">Güvenlik tehditleri ve kötüye kullanımı önlemek</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "data-sharing",
      title: "4. Veri Paylaşımı",
      icon: <Users className="w-5 h-5" />,
      content: (
        <div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20' : 'bg-red-50'} border ${isDark ? 'border-red-700' : 'border-red-200'} mb-4`}>
            <div className="flex items-start">
              <AlertTriangle className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
              <div>
                <h4 className={`font-medium ${isDark ? 'text-red-200' : 'text-red-800'} mb-1`}>
                  Önemli: Veri Satışı Yapmıyoruz
                </h4>
                <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                  Kişisel verilerinizi asla üçüncü taraflara satmayız veya pazarlama amaçlı paylaşmayız.
                </p>
              </div>
            </div>
          </div>

          <p className="mb-4">Verilerinizi yalnızca aşağıdaki durumlarda paylaşırız:</p>
          
          <h4 className="font-medium mb-3">4.1 Hizmet Sağlayıcıları</h4>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Bulut depolama hizmetleri (güvenli veri saklama)</li>
            <li>E-posta hizmet sağlayıcıları (bildirimler için)</li>
            <li>Analitik araçları (anonim kullanım verileri)</li>
          </ul>

          <h4 className="font-medium mb-3">4.2 Yasal Gereklilikler</h4>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Yasal yükümlülüklerimizi yerine getirmek</li>
            <li>Mahkeme kararları ve resmi talepler</li>
            <li>Platform güvenliğini sağlamak</li>
          </ul>
        </div>
      )
    },
    {
      id: "data-security",
      title: "5. Veri Güvenliği",
      icon: <Lock className="w-5 h-5" />,
      content: (
        <div>
          <p className="mb-4">Verilerinizi korumak için çok katmanlı güvenlik önlemleri kullanıyoruz:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className="font-medium mb-2 flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                Şifreleme
              </h4>
              <p className="text-sm">Tüm veriler 256-bit SSL şifreleme ile korunur</p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className="font-medium mb-2 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
                Erişim Kontrolü
              </h4>
              <p className="text-sm">Çok faktörlü kimlik doğrulama ve rol bazlı erişim</p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className="font-medium mb-2 flex items-center">
                <Database className="w-4 h-4 mr-2" />
                Yedekleme
              </h4>
              <p className="text-sm">Düzenli veri yedekleme ve felaketten kurtarma planları</p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className="font-medium mb-2 flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                İzleme
              </h4>
              <p className="text-sm">7/24 güvenlik izleme ve tehdit tespiti</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "user-rights",
      title: "6. Kullanıcı Hakları",
      icon: <FileText className="w-5 h-5" />,
      content: (
        <div>
          <p className="mb-4">KVKK kapsamında aşağıdaki haklara sahipsiniz:</p>
          
          <div className="space-y-3">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${isDark ? 'border-green-700' : 'border-green-200'}`}>
              <h4 className="font-medium mb-1">📄 Bilgi Alma Hakkı</h4>
              <p className="text-sm">Hangi verilerinizin işlendiğini öğrenme hakkı</p>
            </div>
            
            <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${isDark ? 'border-green-700' : 'border-green-200'}`}>
              <h4 className="font-medium mb-1">✏️ Düzeltme Hakkı</h4>
              <p className="text-sm">Yanlış verilerin düzeltilmesini talep etme hakkı</p>
            </div>
            
            <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${isDark ? 'border-green-700' : 'border-green-200'}`}>
              <h4 className="font-medium mb-1">🗑️ Silme Hakkı</h4>
              <p className="text-sm">Verilerinizin silinmesini talep etme hakkı</p>
            </div>
            
            <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'} border ${isDark ? 'border-green-700' : 'border-green-200'}`}>
              <h4 className="font-medium mb-1">📦 Taşınabilirlik Hakkı</h4>
              <p className="text-sm">Verilerinizi başka platformlara taşıma hakkı</p>
            </div>
          </div>
          
          <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'}`}>
            <h4 className="font-medium mb-2">Haklarınızı Kullanmak İçin:</h4>
            <p className="text-sm mb-2">privacy@beetrax.com adresine e-posta gönderin veya destek ekibimizle iletişime geçin.</p>
            <p className="text-sm">Taleplerinizi en geç 30 gün içinde yanıtlayacağız.</p>
          </div>
        </div>
      )
    },
    {
      id: "cookies",
      title: "7. Çerezler (Cookies)",
      icon: <Database className="w-5 h-5" />,
      content: (
        <div>
          <p className="mb-4">Web sitemizde kullanıcı deneyimini geliştirmek için çerezler kullanıyoruz:</p>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">🔧 Gerekli Çerezler</h4>
              <p className="text-sm mb-2">Web sitesinin temel işlevleri için gereklidir.</p>
              <ul className="list-disc pl-6 text-sm space-y-1">
                <li>Oturum yönetimi</li>
                <li>Güvenlik</li>
                <li>Kullanıcı tercihleri</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">📊 Analitik Çerezler</h4>
              <p className="text-sm mb-2">Web sitesi kullanımını anlamak için anonim veriler toplar.</p>
              <ul className="list-disc pl-6 text-sm space-y-1">
                <li>Sayfa görüntülenme sayıları</li>
                <li>Kullanıcı davranış analizi</li>
                <li>Performans ölçümleri</li>
              </ul>
            </div>
          </div>
          
          <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <p className="text-sm">
              <strong>Çerez Kontrolü:</strong> Tarayıcı ayarlarınızdan çerezleri yönetebilir veya devre dışı bırakabilirsiniz. 
              Ancak bu durumda bazı özellikler çalışmayabilir.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "contact",
      title: "8. İletişim",
      icon: <Mail className="w-5 h-5" />,
      content: (
        <div>
          <p className="mb-4">Gizlilik ile ilgili sorularınız için bizimle iletişime geçebilirsiniz:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center mb-3">
                <Mail className="w-5 h-5 mr-2" />
                <h4 className="font-medium">E-posta</h4>
              </div>
              <p className="text-sm mb-1">Genel Sorular:</p>
              <p className="text-sm text-blue-600">info@beetrax.net</p>
              <p className="text-sm mb-1 mt-2">Gizlilik Soruları:</p>
              <p className="text-sm text-blue-600">info@beetrax.net</p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center mb-3">
                <MapPin className="w-5 h-5 mr-2" />
                <h4 className="font-medium">Adres</h4>
              </div>
              <p className="text-sm">
                Beetrax Teknoloji A.Ş.<br/>
                Muallimköy, Deniz Cd. No:143-5<br/>
                41400 Gebze/Kocaeli
              </p>
            </div>
          </div>
          
          <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'} border ${isDark ? 'border-yellow-700' : 'border-yellow-200'}`}>
            <div className="flex items-start">
              <Calendar className={`w-5 h-5 mr-2 mt-0.5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <div>
                <h4 className={`font-medium ${isDark ? 'text-yellow-200' : 'text-yellow-800'} mb-1`}>
                  Yanıt Süresi
                </h4>
                <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  Gizlilik ile ilgili taleplerinizi en geç 30 gün içinde yanıtlayacağız.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`py-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className={`p-2 rounded-full mr-4 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Shield className={`w-7 h-7 mr-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h1 className="text-2xl font-bold">Gizlilik Politikası</h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Son güncelleme: {lastUpdated}
                  </p>
                </div>
              </div>
            </div>
            
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
            }`}>
              KVKK Uyumlu
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Introduction Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <div className="flex items-start">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'} mr-4`}>
                <Shield className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">Beetrax Gizlilik Taahhüdü</h2>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                  Beetrax olarak, kullanıcılarımızın gizliliğini korumayı ve verilerini güvenle saklamayı en önemli 
                  önceliğimiz olarak görüyoruz. Bu politika, verilerinizin nasıl korunduğunu şeffaf bir şekilde açıklar.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Table of Contents */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm mb-8`}
          >
            <h3 className="text-lg font-semibold mb-4">İçindekiler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={`flex items-center p-3 rounded-lg ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  } transition-colors`}
                >
                  <span className={`mr-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    {section.icon}
                  </span>
                  <span className="text-sm font-medium">{section.title}</span>
                </a>
              ))}
            </div>
          </motion.div>

          {/* Content Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 2) }}
                className={`${
                  isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                } border rounded-xl p-6 shadow-sm`}
              >
                <div className="flex items-center mb-4">
                  <span className={`mr-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    {section.icon}
                  </span>
                  <h2 className="text-xl font-bold">{section.title}</h2>
                </div>
                <div className={`${isDark ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                  {section.content}
                </div>
              </motion.section>
            ))}
          </div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className={`mt-12 p-6 rounded-xl ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border text-center`}
          >
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Bu gizlilik politikası {lastUpdated} tarihinde güncellenmiştir. 
              Önemli değişiklikler olduğunda size bilgi vereceğiz.
            </p>
            <div className="flex justify-center mt-4">
              <button
                onClick={() => navigate('/')}
                className={`px-6 py-2 rounded-lg ${
                  isDark 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } transition-colors`}
              >
                Ana Sayfaya Dön
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 